import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigation, useRoute} from '@react-navigation/native';
import isEqual from 'lodash/isEqual';

import Text from '/component/base/Text';
import ReportDefaultLoading from '/component/partial/report/ReportDefaultLoading';
import ReportEmptyState from '/component/partial/report/ReportEmptyState';
import {setIsViewTeam} from '/redux/action/myTeams.action';
import {black} from '/theme/color/on.color';
import {getDefaultNewCoachesKey} from '/util/filter.util';

import {useFlatListData} from './hooks/useFlatListData';
import {useMyTeamsBusiness} from './hooks/useMyTeamsBusiness';
import {useMyTeamsContext} from './hooks/useMyTeamsContext';
import CoachRowItem from './CoachRowItem';
import {CoachInformation} from './TeamOrgReport';
import {styles} from './TeamOrgReportList.style';
import {useCoachList} from './useCoachList';

function MyTeamsReportList({
  onPress,
  item,
  downlineCoachIds,
  filtersData,
  reportParams,
  coachId,
  lastPaidRank,
  highestAchievedRank,
  coachCurrentRank,
  count,
  previousScreenName,
  currentScreenName,
  userId,
  viewTeamsReportFilterCount,
  isViewTeamsLoading,
}: any) {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch();
  const {reduxActiveTab, reduxIsViewTeam} = useMyTeamsContext();

  const [listLoading, setListLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chevronPressed, setIsChevronPressed] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<CoachInformation[]>([]);

  const myTeamsData = useSelector(
    state => state?.myTeams?.viewTeams || [],
    isEqual,
  );

  const {
    viewTeamLoadingId,
    loadingStarId,
    handleViewTeamPress,
    toggleAddWatchlist,
    formatNumber,
    updatedItem,
  } = useMyTeamsBusiness({
    navigation,
    route,
    isViewTeam: reduxIsViewTeam,

    downlineCoachIds,

    myTeamsData,
    setIsLoading,
    onPress,

    filtersData,
    reportParams,

    lastPaidRank,
    highestAchievedRank,
    coachCurrentRank,
    count,
    previousScreenName,
    currentScreenName,
    userId,

    isWatchlistScreen: false,
  });

  const {
    data,
    visibleMap,
    toggleExpand,
    loadingIds,
    childMap,
    loadMore,
    countsMap,
    isPaginating,
  } = useCoachList({
    item,
    currentTeam,
    dispatch,
    filtersData,
    isViewTeam: reduxIsViewTeam,
    reportParams,
    rootCoachId: coachId,
    viewTeamsReportFilterCount,
    setIsChevronPressed,
  });

  const isViewTeamsMode = reduxActiveTab === 'viewTeams';
  const isFiltersDefault = (() => {
    if (!filtersData) return true;

    return (
      (!filtersData.rank || filtersData.rank.length === 0) &&
      (!filtersData.fibc || filtersData.fibc === 'Any') &&
      (!filtersData.period || filtersData.period === getDefaultNewCoachesKey) &&
      !filtersData.frontline &&
      !filtersData.closeToNextRank &&
      !filtersData.cabEarned &&
      !filtersData.aabEarned &&
      filtersData.fqvMin == null &&
      filtersData.fqvMax == null &&
      filtersData.newClientsAbove == null &&
      filtersData.newClientsBelow == null &&
      filtersData.qualifiedClientsAbove == null &&
      filtersData.qualifiedClientsBelow == null &&
      filtersData.newCoachesAbove == null &&
      filtersData.newCoachesBelow == null
    );
  })();
  const {sortBy = '', sortOrder = ''} = reportParams?.[0] || {};
  const isDefaultView =
    (sortBy === 'rank' || sortBy === 'default' || !sortBy) &&
    (sortOrder === 'DESC' || !sortOrder) &&
    isFiltersDefault;

  const hasExpandedDefaultView = useRef(false);
  useEffect(() => {
    if (!hasExpandedDefaultView.current && !isViewTeamsMode && isDefaultView) {
      const rootCoaches = currentTeam.filter(
        coach => (coach.level?.level ?? 0) === 0,
      );

      rootCoaches.forEach(coach => {
        if (coach.isViewTeam || coach.isParentCoach) {
          toggleExpand(coach.id, 0);
        }
      });

      hasExpandedDefaultView.current = true;
    }
  }, [currentTeam, isDefaultView, isViewTeamsMode, toggleExpand]);

  useEffect(() => {
    if (isPaginating || chevronPressed) return;
    if (!myTeamsData || myTeamsData.length === 0) {
      setCurrentTeam([]);
    }
    setIsLoading(true);

    const updatedData = myTeamsData?.map(updatedItem) || [];
    setCurrentTeam(prev => (isEqual(prev, updatedData) ? prev : updatedData));

    setIsLoading(false);
  }, [
    myTeamsData,
    updatedItem,
    filtersData,
    reportParams,
    isPaginating,
    chevronPressed,
  ]);

  useEffect(() => {
    const updateViewTeamState = () => {
      if (reduxActiveTab === 'my-teams') {
        const shouldBeViewTeam = !!coachId;
        dispatch(setIsViewTeam(shouldBeViewTeam));
      } else {
        dispatch(setIsViewTeam(false));
      }
    };

    updateViewTeamState();

    const unsubscribe = navigation.addListener('focus', updateViewTeamState);

    return unsubscribe;
  }, [navigation, reduxActiveTab, coachId, dispatch]);

  const flatListData = useFlatListData({
    data,
    myTeamsData,
    isViewTeamsMode: reduxActiveTab === 'viewTeams',
    isDefaultView,
    updatedItem,
  });

  const renderCoach = useCallback(
    (coach: CoachInformation, level = 0, visited = new Set<string>()) => {
      if (visited.has(coach.id)) return null;
      visited.add(coach.id);

      const frontLineCoach = childMap.get(coach.id) ?? [];

      const totalChildren = countsMap.get(coach.id) ?? 0;
      const visibleChildren = visibleMap.get(coach.id) ?? 0;
      const isExpanded = visibleChildren > 0;
      const shouldShowLoadMore =
        isExpanded &&
        visibleChildren < totalChildren &&
        coach.id !== userId &&
        (!reduxIsViewTeam || (reduxIsViewTeam && level > 0));

      const indent = isDefaultView ? (level >= 3 ? 64 : level * 16) : 0;
      const isInWishlist = downlineCoachIds.includes(String(coach.id));
      const hasTeamMembers =
        (coach.scTeamsCount && coach.scTeamsCount > 0) ||
        childMap.has(coach.id) ||
        (level === 0 && data.length > 1);

      const shouldShowChevron =
        isDefaultView &&
        (coach.isViewTeam ||
          coach.isParentCoach ||
          (level === 0 && hasTeamMembers));

      return (
        <View key={coach.id} style={styles.columnDirection}>
          {<View style={styles.nodeSeparator} />}

          <CoachRowItem
            wishlistTestID={`wishlist-${coach.id}`}
            expandTestID={`expand-${coach.id}`}
            item={coach}
            formatNumber={formatNumber}
            isInWishlist={isInWishlist}
            onToggleWishlist={() => toggleAddWatchlist(coach)}
            indent={indent}
            showChevron={Boolean(shouldShowChevron)}
            isExpanded={isExpanded}
            onToggleExpand={() => toggleExpand(coach.id, level)}
            onPress={onPress}
            onViewTeamPress={handleViewTeamPress}
            viewTeamLoading={viewTeamLoadingId === coach.id}
            level={level}
            showViewTeamButton={!isViewTeamsMode}
            iswatchList={false}
            loading={loadingIds.has(coach.id)}
            starLoading={loadingStarId.has(String(coach.id))}
            tabsValue={reduxActiveTab}
            loginCoachId={userId}
          />

          {isExpanded &&
            frontLineCoach.map((child: CoachInformation) =>
              renderCoach(child, level + 1, visited),
            )}

          {shouldShowLoadMore && (
            <TouchableOpacity
              testID={`load-more-${coach.id}`}
              style={styles.loadMoreButton}
              onPress={async () => {
                setListLoading(true);
                await loadMore(coach.id, level, reduxIsViewTeam);
                setListLoading(false);
              }}>
              {loadingIds.has(coach.id) || listLoading ? (
                <View style={styles.loadMoreLoaderContainer}>
                  <ActivityIndicator size="small" color={black} />
                </View>
              ) : (
                <Text style={styles.loadMoreText} isBold>
                  {reduxIsViewTeam && !isDefaultView
                    ? 'Load More'
                    : `${coach.firstName} ${coach.lastName} Load More`}
                </Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      );
    },
    [
      childMap,
      countsMap,
      visibleMap,
      userId,
      reduxIsViewTeam,
      isDefaultView,
      downlineCoachIds,
      data.length,
      formatNumber,
      onPress,
      handleViewTeamPress,
      viewTeamLoadingId,
      isViewTeamsMode,
      loadingIds,
      loadingStarId,
      reduxActiveTab,
      listLoading,
      toggleAddWatchlist,
      toggleExpand,
      loadMore,
    ],
  );

  const LoadMoreFooter = () => {
    const coach = currentTeam.find(c => c.id === coachId);
    const totalCount = Number(viewTeamsReportFilterCount) || 0;
    const hasMore = currentTeam.length < totalCount;

    if (!hasMore) return null;
    return (
      <View style={styles.loadMoreFooter}>
        {listLoading ? (
          <ReportDefaultLoading total={3} />
        ) : (
          <TouchableOpacity
            testID="load-more-footer"
            style={styles.loadMoreButton}
            activeOpacity={0.7}
            delayPressIn={0}
            hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
            onPress={async () => {
              setListLoading(true);
              await loadMore(coachId, 0, reduxIsViewTeam);
              setListLoading(false);
            }}>
            <Text style={styles.loadMoreText} isBold>
              {reduxIsViewTeam && isDefaultView && coach
                ? `${coach.firstName} ${coach.lastName} Load More`
                : 'Load More'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const FooterWrapper = React.memo(() => <LoadMoreFooter />);

  let content = null;

  if (
    reduxIsViewTeam &&
    !isPaginating &&
    !chevronPressed &&
    (isViewTeamsLoading || (isLoading && flatListData.length === 0))
  ) {
    content = <ReportDefaultLoading total={3} />;
  } else if (
    reduxIsViewTeam &&
    !isViewTeamsLoading &&
    !isLoading &&
    flatListData.length === 0
  ) {
    content = <ReportEmptyState />;
  } else if (flatListData.length > 0) {
    if (Platform.OS === 'web') {
      content = (
        <View style={styles.container}>
          {flatListData.map(item => renderCoach(item, 0))}
          {reduxIsViewTeam ? <FooterWrapper /> : <View style={{height: 100}} />}
        </View>
      );
    } else {
      content = (
        <FlatList
          contentContainerStyle={styles.container}
          data={flatListData}
          renderItem={({item}) => renderCoach(item, 0)}
          keyExtractor={item => `${item.id}`}
          onEndReachedThreshold={0.5}
          ListFooterComponent={reduxIsViewTeam ? <FooterWrapper /> : null}
        />
      );
    }
  }

  return <>{content}</>;
}

export default React.memo(MyTeamsReportList);
